import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '../../../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select';
import { organizationsAPI, locationsAPI } from '../../../lib/api';

/**
 * OrgLocationPicker — the single, reusable Organization → Location context
 * selector for the Surveys module. The PORTAL owns the URL; this picker only
 * captures the ownership context (organization_id + location_id) that the
 * backend uses to auto-generate the public URL.
 *
 * Controlled: parent owns `organizationId` / `locationId`.
 *   - onChange({ organizationId, locationId }) fires on every change
 *     (user selection AND auto-selection). Changing the organization resets
 *     the location to null so a cross-org location can never persist.
 *   - onMeta({ loaded, orgCount, locationCount }) reports list sizes so the
 *     parent can enforce "multi-location org must pick a location".
 */
export const OrgLocationPicker = ({
    organizationId,
    locationId,
    onChange,
    onMeta,
    disabled = false,
    testIdPrefix = 'survey',
}) => {
    const { t } = useTranslation();
    const [orgs, setOrgs] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        Promise.all([
            organizationsAPI.list().catch(() => ({ data: [] })),
            locationsAPI.list().catch(() => ({ data: [] })),
        ]).then(([orgRes, locRes]) => {
            if (!alive) return;
            const orgList = Array.isArray(orgRes?.data) ? orgRes.data : (orgRes?.data?.organizations || []);
            const locList = Array.isArray(locRes?.data) ? locRes.data : (locRes?.data?.locations || []);
            setOrgs(orgList);
            setLocations(locList);
            setLoaded(true);
            // Auto-select the only organization the caller can manage.
            if (!organizationId && orgList.length === 1) {
                onChange?.({ organizationId: orgList[0].id, locationId: null });
            }
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const orgLocations = useMemo(
        () => locations.filter((l) => l.organization_id === organizationId),
        [locations, organizationId],
    );

    // Auto-select the only location for the chosen organization.
    useEffect(() => {
        if (organizationId && !locationId && orgLocations.length === 1) {
            onChange?.({ organizationId, locationId: orgLocations[0].id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, orgLocations.length]);

    // Report list sizes upward so the parent can validate.
    useEffect(() => {
        onMeta?.({ loaded, orgCount: orgs.length, locationCount: orgLocations.length });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, orgs.length, orgLocations.length]);

    const orgLabel = (o) => o.display_name || o.name || o.id;
    const orgLocked = disabled || orgs.length <= 1;

    return (
        <div className="space-y-4" data-testid={`${testIdPrefix}-org-location-picker`}>
            <div>
                <Label className="mb-2 block">
                    {t('surveys.organization', 'Organization')} <span className="text-red-500">*</span>
                </Label>
                <Select
                    value={organizationId || ''}
                    onValueChange={(v) => onChange?.({ organizationId: v, locationId: null })}
                    disabled={orgLocked}
                >
                    <SelectTrigger data-testid={`${testIdPrefix}-organization-select`}>
                        <SelectValue placeholder={t('surveys.selectOrganization', 'Select an organization')} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                        {orgs.map((o) => (
                            <SelectItem key={o.id} value={o.id}>{orgLabel(o)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {loaded && orgs.length <= 1 && (
                    <p className="text-xs text-muted-foreground mt-1">
                        {t('surveys.orgAutoHelp', 'Your surveys are automatically attached to your organization.')}
                    </p>
                )}
            </div>

            {organizationId && orgLocations.length > 0 && (
                <div>
                    <Label className="mb-2 block">
                        {t('surveys.selectLocation', 'Select location')}
                        {orgLocations.length > 1 && <span className="text-red-500"> *</span>}
                    </Label>
                    <Select
                        value={locationId || ''}
                        onValueChange={(v) => onChange?.({ organizationId, locationId: v })}
                        disabled={disabled}
                    >
                        <SelectTrigger data-testid={`${testIdPrefix}-location-select`}>
                            <SelectValue placeholder={t('surveys.selectLocationPlaceholder', 'Choose a location')} />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                            {orgLocations.map((l) => (
                                <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </div>
    );
};

export default OrgLocationPicker;
